import type { DashboardViewModel } from "../app/dashboardViewModel";
import { formatGrind, formatUncertainty } from "../app/grindFormat";
import { hasData } from "../engine/model";
import { Legend, TrendChart } from "./TrendChart";

/**
 * "Recommended grind: 7.4 ± 0.3" (or "9E ± 2 letters" on a macro/micro dial), "Based on N shots · target X s", the explanation (only what is new: last shot's
 * direction, starting-point source, bound / dial-limit notes), the grinder chip, the Log shot button, and the
 * collapsible chart.
 */
export function PredictionCard(props: { vm: DashboardViewModel; onGrinderChip: () => void; onLogShot: () => void }) {
  const { vm } = props;
  const p = vm.prediction.value;
  const g = vm.selectedGrinder.value;
  const empty = vm.emptyState.value;
  const delta = vm.lastShotDelta.value;
  const history = vm.activeHistory.value; // the chart shows only shots the prediction uses

  let setting = "—", uncertainty = "", explanation = empty, flag = "", muted = true;
  if (p && g) {
    setting = formatGrind(p.grindSetting, g);
    muted = !hasData(p);
    uncertainty = formatUncertainty(p.uncertainty, g);
    explanation = p.explanation;
    const flags: string[] = [];
    if (p.atBound) flags.push("At the end of the dial range");
    if (p.conflictingBounds) flags.push("Choked and channeled shots conflict");
    flag = flags.join(" · ");
  }

  return (
    <section class="card prediction" aria-label="Recommendation">
      <div class="rec-top">
        <div class="grow">
          <div class="rec-label">Recommended grind</div>
          <div class="headline" aria-live="polite">
            <span class={muted ? "setting muted" : "setting"}>{setting}</span>
            {uncertainty && <span class="uncertainty">{uncertainty}</span>}
          </div>
        </div>
        {g && <button type="button" class="chip" aria-label={`Grinder: ${g.name}. Change grinder`} onClick={props.onGrinderChip}>{vm.grinderChip.value} {"▾"}</button>}
      </div>
      {vm.subtitle.value && <div class="subtitle">{vm.subtitle.value}</div>}
      {explanation && <div class="explanation">{explanation}</div>}
      {flag && <div class="flag">{flag}</div>}
      {delta && <div class="delta">{delta}</div>}
      {vm.nudgeNote.value && <div class="delta nudge" role="status">{vm.nudgeNote.value}</div>}
      {vm.calibrationPrompt.value && <div class="calibrate">{vm.calibrationPrompt.value}</div>}
      <button type="button" class="primary" onClick={props.onLogShot} disabled={!vm.selectedBean.value}>Log shot</button>

      {p && g && history.length > 0 && (
        <>
          <button type="button" class="ghost chart-toggle" aria-expanded={vm.chartOpen.value} onClick={() => void vm.toggleChart()}>
            {vm.chartOpen.value ? "Hide chart" : "Show chart"}
          </button>
          {vm.chartOpen.value && (
            <div class="chart">
              <TrendChart shotsNewestFirst={history} prediction={p} grinder={g} cfg={vm.effectiveConfigForBean} />
              <Legend />
              <div class="chart-footer">Dial-in History &amp; Recommendation Trend</div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
